import { useEffect, useRef, useState, createContext } from "react";
import { motion, useInView, useAnimationFrame } from "framer-motion";
import Lenis from "lenis";

export const ASSETS = {
  hero: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/5d2a13150b4596f0ff691831f2d3df9eeb47d7cae29b135cf7743a3e654df015.jpeg",
  metro: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/5a6d47c2b2ed314f9d050bc28c8605b0241c81083b7d54d7e64662b9815fd125.jpeg",
  cluLand: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/9d9bbb04e93d1dc6d5f78cac5d9b91a4aa6eaa227b3ee9834bcdd9951cb9dee3.jpeg",
  dataTxn: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/807ce0a134b62a054fb809f546f4f6511391b4bb85d7557d9f1567721c78f28b.jpeg",
  dataTender: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/04a2d44a8fb275d1f527c4111cc291dc4310a62efeee13a4617a542bfe19db5a.jpeg",
  success: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/dc32bc20fa1210a79e24cd1f475fbeabd5d6a267d1a337f95a11b9f2aa0d0f4d.jpeg",
  registry: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/746878b198a3f526b42abe16de86de05286b182462c0440466a0085950040ee2.jpeg",
  ayodhyaHero: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/d9ea5fb61b97d16493c38015dd6ed84f227a0f4a9afad0279a5b68bde565fc5c.jpeg",
  ayodhyaInfra: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/0c80b0f25913ec807d6a8aa5a7cf2ce89405b51cbb5272bdedb253afc9bb4902.jpeg",
  groundVerify: "https://static.prod-images.emergentagent.com/jobs/fab6e68c-d7f9-4f60-aea4-fd3fbd6c454d/images/9c16ab7f5c9a9f8dcca02aab3007261b6ce65c9de7f42394d73892cf217c3e6c.jpeg",
  logo: "/assets/bwg-logo.png",
  logoWhite: "/assets/bwg-logo-white.png",
  logoInk: "/assets/bwg-logo-ink.png",
  team: "/assets/team.jpeg",
  founderSahil: "/assets/founder-sahil.jpg",
  toi: "/assets/toi-logo.png",
  deccan: "/assets/deccan-herald.png",
  fe: "/assets/financial-express.svg",
  moneycontrol: "/assets/moneycontrol-logo.png",
  tribune: "/assets/tribune-india.webp",
  ani: "/assets/ani-news.png",
  silicon: "/assets/silicon-india.png",
};

export const WHATSAPP = "https://api.whatsapp.com/send/?phone=%2B919971169695&text=Hello%2C+I+Would+Like+to+Book+a+Consultation+Call&type=phone_number&app_absent=0";
export const PHONE = "+91-9971169695";
export const EMAIL = "info@bhaaratwealthgroup.com";

export const LenisContext = createContext(null);

export function SmoothScroll({ children }) {
  const lenisRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);
  return <LenisContext.Provider value={lenisRef}>{children}</LenisContext.Provider>;
}

// Scroll reveal wrapper
export function Reveal({ children, delay = 0, y = 40, className = "", as = "div" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </MotionTag>
  );
}

// Masked line-by-line hero reveal
export function MaskLine({ children, delay = 0 }) {
  return (
    <span className="line-mask">
      <motion.span
        style={{ display: "block" }}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// Count-up animated number.
// SEO/crawler-safe: initial render is the REAL final value, so raw HTML and
// non-scrolling crawlers (GPTBot, ClaudeBot, Bingbot, etc.) see true numbers.
// The count-up from 0 is layered on top purely as a visual enhancement when
// the element scrolls into view in a real browser.
export function CountUp({ end, suffix = "", prefix = "", decimals = 0, duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(end);
  const start = useRef(null);
  useAnimationFrame((t) => {
    if (!inView) return;
    if (start.current === null) start.current = t;
    const p = Math.min((t - start.current) / (duration * 1000), 1);
    const eased = 1 - Math.pow(1 - p, 3);
    setVal(end * eased);
  });
  const display = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}
